import { Controller } from "@hotwired/stimulus";
import {
  computePosition,
  offset,
  shift,
  autoUpdate,
} from "@floating-ui/dom";

// Single-input async typeahead: type in the field, debounced remote search fills
// a results panel (server HTML). Selection writes a hidden value and dispatches
// `app-select:change` so existing modal listeners keep working.
export default class extends Controller {
  static targets = [
    "input",
    "panel",
    "hidden",
    "clear",
    "results",
    "inviteForm",
    "inviteFirstName",
    "inviteLastName",
    "inviteEmail",
    "inviteError",
    "inviteSubmit",
  ];
  static values = {
    searchUrl: String,
    inviteUrl: String,
    allowInvite: Boolean,
    searchDelay: { type: Number, default: 200 },
    openClass: { type: String, default: "async-typeahead-open" },
  };

  connect() {
    this.isOpen = false;
    this.highlightIndex = -1;
    this.searchTimer = null;
    this.abortController = null;
    this.cleanupFloating = null;
    this.isInviting = false;
    this._selectedLabel = this.inputTarget.value || "";
    this._updateClearVisibility();
  }

  disconnect() {
    this._cancelSearch();
    this._unbindOutsideEvents();
    this._teardownFloating();
  }

  // Public — used by parent modal controllers on reset without firing change.
  clear({ dispatch = true } = {}) {
    this._selectedLabel = "";
    this.inputTarget.value = "";
    if (this.hasHiddenTarget) this.hiddenTarget.value = "";
    this._updateClearVisibility();
    this.close();
    if (dispatch) this._dispatchChange({ value: "", email: "" });
  }

  clearClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.clear({ dispatch: true });
    this.inputTarget.focus();
  }

  onFocus() {
    const query = this.inputTarget.value.trim();
    const hasSelection = this.hasHiddenTarget && this.hiddenTarget.value;
    if (query.length > 0 && !hasSelection) {
      this._scheduleSearch();
    } else if (this.panelTarget.childElementCount > 0 && query.length > 0 && !hasSelection) {
      this.open();
    }
  }

  onInput() {
    // Typing after a selection invalidates it until a new pick.
    if (this.hasHiddenTarget && this.hiddenTarget.value) {
      this.hiddenTarget.value = "";
      this._selectedLabel = "";
      this._updateClearVisibility();
      this._dispatchChange({ value: "", email: "" });
    }

    this._scheduleSearch();
  }

  onKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (!this.isOpen) {
          this._scheduleSearch({ immediate: true });
          return;
        }
        this._moveHighlight(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        if (!this.isOpen) return;
        this._moveHighlight(-1);
        break;
      case "Enter":
        if (!this.isOpen) return;
        event.preventDefault();
        if (this.highlightIndex >= 0) {
          this._options()[this.highlightIndex]?.click();
        }
        break;
      case "Escape":
        if (!this.isOpen) return;
        event.preventDefault();
        event.stopPropagation();
        // While inviting, Escape backs out to the results rather than the whole panel.
        if (this._inviteFormOpen()) {
          this.hideInvite();
          return;
        }
        this._restoreSelectedLabel();
        this.close();
        break;
      case "Tab":
        this._restoreSelectedLabel();
        this.close();
        break;
    }
  }

  selectOption(event) {
    this._applySelection(event.currentTarget.dataset);
  }

  _applySelection(dataset) {
    const value = dataset.value || "";
    const label = dataset.label || "";
    const email = dataset.email || "";

    this._selectedLabel = label;
    this.inputTarget.value = label;
    if (this.hasHiddenTarget) this.hiddenTarget.value = value;
    this._updateClearVisibility();
    this.close();
    this._dispatchChange({ ...dataset, value, email });
  }

  // ----- inline invite -----

  showInvite(event) {
    if (event) event.preventDefault();
    if (!this.hasInviteFormTarget) return;

    if (this.hasResultsTarget) this.resultsTarget.classList.add("d-none");
    this.inviteFormTarget.classList.remove("d-none");
    this.highlightIndex = -1;
    this._updateHighlighted();
    this._focusFirstEmptyInviteField();
  }

  hideInvite(event) {
    if (event) event.preventDefault();
    if (!this.hasInviteFormTarget) return;

    this.inviteFormTarget.classList.add("d-none");
    if (this.hasResultsTarget) this.resultsTarget.classList.remove("d-none");
    this.inputTarget.focus();
  }

  async submitInvite(event) {
    if (event) event.preventDefault();
    if (!this.inviteUrlValue || this.isInviting) return;

    this.isInviting = true;
    this._setInviteSubmitting(true);

    try {
      const body = new URLSearchParams({
        first_name: this._inviteFieldValue(this.hasInviteFirstNameTarget && this.inviteFirstNameTarget),
        last_name: this._inviteFieldValue(this.hasInviteLastNameTarget && this.inviteLastNameTarget),
        email: this._inviteFieldValue(this.hasInviteEmailTarget && this.inviteEmailTarget),
      });

      const response = await fetch(this.inviteUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "text/html",
          "X-CSRF-Token": this._csrfToken(),
        },
        credentials: "same-origin",
        body,
      });

      const html = await response.text();
      this.panelTarget.innerHTML = html;

      // Select straight from the returned row's dataset: Stimulus binds actions on
      // injected markup asynchronously, so clicking it here would be a no-op.
      const invited = this.panelTarget.querySelector("[data-auto-select]");
      if (invited) {
        this._applySelection(invited.dataset);
        return;
      }

      this._focusFirstEmptyInviteField();
    } catch {
      this._showInviteError("That invitation could not be sent. Please try again.");
    } finally {
      this.isInviting = false;
      this._setInviteSubmitting(false);
    }
  }

  _inviteFormOpen() {
    return this.hasInviteFormTarget && !this.inviteFormTarget.classList.contains("d-none");
  }

  _inviteFieldValue(field) {
    return field ? field.value.trim() : "";
  }

  _focusFirstEmptyInviteField() {
    const fields = [
      this.hasInviteFirstNameTarget && this.inviteFirstNameTarget,
      this.hasInviteLastNameTarget && this.inviteLastNameTarget,
      this.hasInviteEmailTarget && this.inviteEmailTarget,
    ].filter(Boolean);

    const target = fields.find((field) => field.value.trim() === "") || fields[0];
    target?.focus();
  }

  _setInviteSubmitting(submitting) {
    if (this.hasInviteSubmitTarget) this.inviteSubmitTarget.disabled = submitting;
  }

  _showInviteError(message) {
    if (!this.hasInviteErrorTarget) return;
    this.inviteErrorTarget.textContent = message;
    this.inviteErrorTarget.classList.remove("d-none");
  }

  _csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || "";
  }

  open() {
    if (this.isOpen) return;
    this.isOpen = true;

    this.panelTarget.hidden = false;
    this.inputTarget.setAttribute("aria-expanded", "true");
    this.element.classList.add(this.openClassValue);

    this.cleanupFloating = autoUpdate(
      this.inputTarget.closest(".async-typeahead-input-wrap") || this.inputTarget,
      this.panelTarget,
      () => this._positionPanel()
    );

    this._bindOutsideEvents();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.panelTarget.hidden = true;
    this.inputTarget.setAttribute("aria-expanded", "false");
    this.element.classList.remove(this.openClassValue);
    this.highlightIndex = -1;
    this._updateHighlighted();
    this._teardownFloating();
    this._resetPanelPosition();
    this._unbindOutsideEvents();
  }

  _scheduleSearch({ immediate = false } = {}) {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    const delay = immediate ? 0 : this.searchDelayValue;
    this.searchTimer = setTimeout(() => this._runSearch(), delay);
  }

  async _runSearch() {
    const query = this.inputTarget.value.trim();
    if (query === "") {
      this.panelTarget.innerHTML = "";
      this.close();
      return;
    }

    this._cancelInFlight();
    this.abortController = new AbortController();

    try {
      const url = new URL(this.searchUrlValue, window.location.origin);
      url.searchParams.set("query", query);
      if (this.allowInviteValue) url.searchParams.set("invite", "1");
      const response = await fetch(url, {
        headers: { Accept: "text/html" },
        credentials: "same-origin",
        signal: this.abortController.signal,
      });
      if (!response.ok) return;

      this.panelTarget.innerHTML = await response.text();
      this.highlightIndex = -1;
      this.open();
      this._updateHighlighted();
    } catch (error) {
      if (error?.name === "AbortError") return;
      // Transient failures — admin can retype to retry.
    }
  }

  _cancelSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = null;
    this._cancelInFlight();
  }

  _cancelInFlight() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  _restoreSelectedLabel() {
    if (this.hasHiddenTarget && this.hiddenTarget.value && this._selectedLabel) {
      this.inputTarget.value = this._selectedLabel;
    }
  }

  _dispatchChange(detail = {}) {
    if (!this.hasHiddenTarget) return;
    this.hiddenTarget.dispatchEvent(
      new CustomEvent("app-select:change", {
        bubbles: true,
        detail,
      })
    );
  }

  _updateClearVisibility() {
    if (!this.hasClearTarget) return;
    const hasValue = this.hasHiddenTarget && !!this.hiddenTarget.value;
    this.clearTarget.classList.toggle("d-none", !hasValue);
  }

  _bindOutsideEvents() {
    this._outsideClickHandler = (event) => {
      if (!this.element.contains(event.target)) {
        this._restoreSelectedLabel();
        this.close();
      }
    };
    document.addEventListener("click", this._outsideClickHandler);
  }

  _unbindOutsideEvents() {
    if (this._outsideClickHandler) {
      document.removeEventListener("click", this._outsideClickHandler);
      this._outsideClickHandler = null;
    }
  }

  _positionPanel() {
    const anchor =
      this.inputTarget.closest(".async-typeahead-input-wrap") || this.inputTarget;

    return computePosition(anchor, this.panelTarget, {
      placement: "bottom-start",
      strategy: "absolute",
      middleware: [offset(4), shift({ padding: 8 })],
    }).then(({ x, y }) => {
      Object.assign(this.panelTarget.style, {
        left: `${x}px`,
        top: `${y}px`,
        minWidth: `${anchor.offsetWidth}px`,
      });
    });
  }

  _resetPanelPosition() {
    this.panelTarget.style.left = "";
    this.panelTarget.style.top = "";
    this.panelTarget.style.minWidth = "";
  }

  _teardownFloating() {
    if (this.cleanupFloating) {
      this.cleanupFloating();
      this.cleanupFloating = null;
    }
  }

  _moveHighlight(delta) {
    const options = this._options();
    if (!options.length) return;

    let next = this.highlightIndex + delta;
    if (next < 0) next = options.length - 1;
    if (next >= options.length) next = 0;

    this.highlightIndex = next;
    this._updateHighlighted();
    options[next].scrollIntoView({ block: "nearest" });
  }

  _updateHighlighted() {
    this._options().forEach((el, index) => {
      el.classList.toggle("app-select-option-active", index === this.highlightIndex);
    });
  }

  _options() {
    return Array.from(this.panelTarget.querySelectorAll(".app-select-option"));
  }
}
